import React, { useMemo, useState } from 'react';
import { Target, Settings } from 'lucide-react';
import { getCosClassName, getCpaClassName, getMetricLabel } from '../utils/metricsUtils';
import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
import { colonnesVideoEnCellules, cumulerVideo, TOTAUX_VIDEO_INITIAUX } from '../utils/metriquesVideo';
import CampaignTypesCharts, { getCampaignTypeColor } from './CampaignTypesCharts';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

/**
 * Section « Types de campagnes », commune aux dashboards Google Ads et Microsoft Ads.
 *
 * L'appelant fournit les campagnes déjà filtrées et la fonction qui donne le type
 * d'une campagne : Google Ads l'encode en nombre, Microsoft Ads en chaîne. Tout le
 * reste — regroupement, tri, totaux, colonnes configurables, comparaison — vit ici,
 * pour que les deux régies ne divergent pas comme l'ont fait les tableaux de variation.
 */

// Même présentation que les autres tableaux : la variation en ligne, à côté de la valeur.
const renderVariation = makeRenderVariation({
  fontSize: '11px',
  style: { display: 'inline', marginTop: 0, marginLeft: '4px', fontWeight: '500' }
});

const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'];

const CampaignTypesSection = ({
  campaigns = [],
  previousCampaigns = null,
  compareEnabled = false,
  getCampaignType,
  metrics,
  moduleId,
  accountId = 'global',
  componentTitle = 'Types de campagnes',
  // Identifiant du module de visibilité client, distinct de celui des métriques.
  visibilityModuleId = 'campaign_types',
  formatNumber,
  userRole = 'USER',
  isClientModuleVisible = () => true,
  toggleClientModuleVisibility = () => {},
  onTypeClick = null,
  loading = false,
  // Vrai pendant le chargement des données de comparaison : on masque le tableau
  // plutôt que d'afficher des variations partielles.
  loadingComparison = false,
  showCharts = true
}) => {
  const [showConfig, setShowConfig] = useState(false);

  // Configuration des colonnes : ordre, libellés, rendu de la cellule et du total.
  const metricConfig = useMemo(() => ({
    impressions: {
      sortKey: 'impressions',
      defaultLabel: 'Impr.',
      render: (type, prevType) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div>{formatNumber(type.impressions || 0)}</div>
          {prevType && renderVariation(calculateVariation(type.impressions, prevType.impressions))}
        </td>
      ),
      renderTotal: (totals, prevTotals) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div><strong>{formatNumber(totals.impressions)}</strong></div>
          {prevTotals && renderVariation(calculateVariation(totals.impressions, prevTotals.impressions))}
        </td>
      )
    },
    ctr: {
      sortKey: 'ctr',
      defaultLabel: 'CTR',
      render: (type, prevType) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div>{formatNumber(type.ctr || 0, 2)}%</div>
          {prevType && renderVariation(calculateVariation(type.ctr, prevType.ctr))}
        </td>
      ),
      renderTotal: (totals, prevTotals) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div><strong>{formatNumber(totals.ctr, 2)}%</strong></div>
          {prevTotals && renderVariation(calculateVariation(totals.ctr, prevTotals.ctr))}
        </td>
      )
    },
    clics: {
      sortKey: 'clicks',
      defaultLabel: 'Clics',
      render: (type, prevType) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div>{formatNumber(type.clicks || 0)}</div>
          {prevType && renderVariation(calculateVariation(type.clicks, prevType.clicks))}
        </td>
      ),
      renderTotal: (totals, prevTotals) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div><strong>{formatNumber(totals.clicks)}</strong></div>
          {prevTotals && renderVariation(calculateVariation(totals.clicks, prevTotals.clicks))}
        </td>
      )
    },
    cpc: {
      sortKey: 'cpc',
      defaultLabel: 'CPC',
      render: (type, prevType) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div>€{formatNumber(type.cpc || 0, 2)}</div>
          {prevType && renderVariation(calculateVariation(type.cpc, prevType.cpc), true)}
        </td>
      ),
      renderTotal: (totals, prevTotals) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div><strong>€{formatNumber(totals.cpc, 2)}</strong></div>
          {prevTotals && renderVariation(calculateVariation(totals.cpc, prevTotals.cpc), true)}
        </td>
      )
    },
    cout: {
      sortKey: 'cost',
      defaultLabel: 'Coût',
      render: (type, prevType) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div>€{formatNumber(type.cost || 0, 2)}</div>
          {prevType && renderVariation(calculateVariation(type.cost, prevType.cost), true)}
        </td>
      ),
      renderTotal: (totals, prevTotals) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div><strong>€{formatNumber(totals.cost, 2)}</strong></div>
          {prevTotals && renderVariation(calculateVariation(totals.cost, prevTotals.cost), true)}
        </td>
      )
    },
    taux_conv: {
      sortKey: 'conversionRate',
      defaultLabel: 'Taux Conv.',
      render: (type, prevType) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div>{formatNumber(type.conversionRate || 0, 2)}%</div>
          {prevType && renderVariation(calculateVariation(type.conversionRate, prevType.conversionRate))}
        </td>
      ),
      renderTotal: (totals, prevTotals) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div><strong>{formatNumber(totals.conversionRate, 2)}%</strong></div>
          {prevTotals && renderVariation(calculateVariation(totals.conversionRate, prevTotals.conversionRate))}
        </td>
      )
    },
    conv: {
      sortKey: 'conversions',
      defaultLabel: 'Conv.',
      render: (type, prevType) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div>{formatNumber(type.conversions || 0, 1)}</div>
          {prevType && renderVariation(calculateVariation(type.conversions, prevType.conversions))}
        </td>
      ),
      renderTotal: (totals, prevTotals) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div><strong>{formatNumber(totals.conversions, 1)}</strong></div>
          {prevTotals && renderVariation(calculateVariation(totals.conversions, prevTotals.conversions))}
        </td>
      )
    },
    cpa: {
      sortKey: 'cpa',
      defaultLabel: 'CPA',
      render: (type, prevType) => (
        <td className={`numeric ${getCpaClassName(type.cpa, accountId, moduleId)}`} style={{ verticalAlign: 'top' }}>
          <div>€{formatNumber(type.cpa || 0, 2)}</div>
          {prevType && renderVariation(calculateVariation(type.cpa, prevType.cpa), true)}
        </td>
      ),
      renderTotal: (totals, prevTotals) => (
        <td className={`numeric ${getCpaClassName(totals.cpa, accountId, moduleId)}`} style={{ verticalAlign: 'top' }}>
          <div><strong>€{formatNumber(totals.cpa || 0, 2)}</strong></div>
          {prevTotals && renderVariation(calculateVariation(totals.cpa, prevTotals.cpa), true)}
        </td>
      )
    },
    valeur_conv: {
      sortKey: 'conversionsValue',
      defaultLabel: 'Val.Conv',
      render: (type, prevType) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div>€{formatNumber(type.conversionsValue || 0, 2)}</div>
          {prevType && renderVariation(calculateVariation(type.conversionsValue, prevType.conversionsValue))}
        </td>
      ),
      renderTotal: (totals, prevTotals) => (
        <td className="numeric" style={{ verticalAlign: 'top' }}>
          <div><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></div>
          {prevTotals && renderVariation(calculateVariation(totals.conversionsValue, prevTotals.conversionsValue))}
        </td>
      )
    },
    cos: {
      sortKey: 'cos',
      defaultLabel: 'COS',
      render: (type, prevType) => (
        <td className={`numeric ${getCosClassName(type.cos, accountId, moduleId)}`} style={{ verticalAlign: 'top' }}>
          <div>{formatNumber(type.cos || 0, 1)}%</div>
          {prevType && renderVariation(calculateVariation(type.cos, prevType.cos), true)}
        </td>
      ),
      renderTotal: (totals, prevTotals) => (
        <td className={`numeric ${getCosClassName(totals.cos, accountId, moduleId)}`} style={{ verticalAlign: 'top' }}>
          <div><strong>{formatNumber(totals.cos, 1)}%</strong></div>
          {prevTotals && renderVariation(calculateVariation(totals.cos, prevTotals.cos), true)}
        </td>
      )
    },
    panier_moyen: {
      sortKey: 'averageOrderValue',
      defaultLabel: 'Panier moy.',
      render: (type, prevType) => {
        const aov = type.conversions > 0 ? type.conversionsValue / type.conversions : 0;
        const prevAov = prevType && prevType.conversions > 0 ? prevType.conversionsValue / prevType.conversions : 0;
        return (
          <td className="numeric" style={{ verticalAlign: 'top' }}>
            <div>€{formatNumber(aov, 2)}</div>
            {prevType && renderVariation(calculateVariation(aov, prevAov))}
          </td>
        );
      },
      renderTotal: (totals, prevTotals) => {
        const aov = totals.conversions > 0 ? totals.conversionsValue / totals.conversions : 0;
        const prevAov = prevTotals && prevTotals.conversions > 0 ? prevTotals.conversionsValue / prevTotals.conversions : 0;
        return (
          <td className="numeric" style={{ verticalAlign: 'top' }}>
            <div><strong>€{formatNumber(aov, 2)}</strong></div>
            {prevTotals && renderVariation(calculateVariation(aov, prevAov))}
          </td>
        );
      }
    },
    roas: {
      sortKey: 'roas',
      defaultLabel: 'ROAS',
      render: (type, prevType) => {
        const roas = type.cost > 0 ? (type.conversionsValue / type.cost) * 100 : 0;
        const prevRoas = prevType && prevType.cost > 0 ? (prevType.conversionsValue / prevType.cost) * 100 : 0;
        return (
          <td className="numeric" style={{ verticalAlign: 'top' }}>
            <div>{formatNumber(roas || 0, 0)}%</div>
            {prevType && renderVariation(calculateVariation(roas, prevRoas))}
          </td>
        );
      },
      renderTotal: (totals, prevTotals) => {
        const roas = totals.cost > 0 ? (totals.conversionsValue / totals.cost) * 100 : 0;
        const prevRoas = prevTotals && prevTotals.cost > 0 ? (prevTotals.conversionsValue / prevTotals.cost) * 100 : 0;
        return (
          <td className="numeric" style={{ verticalAlign: 'top' }}>
            <div><strong>{formatNumber(roas || 0, 0)}%</strong></div>
            {prevTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </td>
        );
      }
    },
    roi: {
      sortKey: 'roi',
      defaultLabel: 'ROI',
      render: (type, prevType) => {
        const hasData = type.cost > 0 && type.conversionsValue > 0;
        const roi = hasData ? (type.conversionsValue - type.cost) / type.cost : 0;
        const prevHasData = prevType && prevType.cost > 0 && prevType.conversionsValue > 0;
        const prevRoi = prevHasData ? (prevType.conversionsValue - prevType.cost) / prevType.cost : 0;
        return (
          <td className="numeric" style={{ verticalAlign: 'top' }}>
            <div>{formatNumber(roi, 2)}</div>
            {prevType && renderVariation(calculateVariation(roi, prevRoi))}
          </td>
        );
      },
      renderTotal: (totals, prevTotals) => {
        const hasData = totals.cost > 0 && totals.conversionsValue > 0;
        const roi = hasData ? (totals.conversionsValue - totals.cost) / totals.cost : 0;
        const prevHasData = prevTotals && prevTotals.cost > 0 && prevTotals.conversionsValue > 0;
        const prevRoi = prevHasData ? (prevTotals.conversionsValue - prevTotals.cost) / prevTotals.cost : 0;
        return (
          <td className="numeric" style={{ verticalAlign: 'top' }}>
            <div><strong>{formatNumber(roi, 2)}</strong></div>
            {prevTotals && renderVariation(calculateVariation(roi, prevRoi))}
          </td>
        );
      }
    },
    ...colonnesVideoEnCellules({ formatNumber, renderVariation, calculateVariation })
  }), [accountId, moduleId, formatNumber]);

  // Regroupe des campagnes par type et calcule les métriques dérivées.
  const groupByType = (list) => {
    const groups = {};
    (list || []).forEach((campaign) => {
      const type = getCampaignType(campaign);
      if (!groups[type]) {
        groups[type] = {
          type, impressions: 0, clicks: 0, cost: 0,
          conversions: 0, conversionsValue: 0, campaignCount: 0,
          ...TOTAUX_VIDEO_INITIAUX
        };
      }
      const group = groups[type];
      group.impressions += campaign.impressions || 0;
      group.clicks += campaign.clicks || 0;
      group.cost += campaign.cost || 0;
      group.conversions += campaign.conversions || 0;
      group.conversionsValue += campaign.conversionsValue || 0;
      Object.assign(group, cumulerVideo(group, campaign));
      group.campaignCount += 1;
      group.ctr = group.impressions > 0 ? (group.clicks / group.impressions * 100) : 0;
      group.cpc = group.clicks > 0 ? (group.cost / group.clicks) : 0;
      group.conversionRate = group.clicks > 0 ? (group.conversions / group.clicks * 100) : 0;
      group.cpa = group.conversions > 0 ? (group.cost / group.conversions) : 0;
      group.cos = group.conversionsValue > 0 ? (group.cost / group.conversionsValue * 100) : 0;
    });
    return groups;
  };

  const typeGroups = useMemo(() => groupByType(campaigns), [campaigns, getCampaignType]);
  const previousTypeGroups = useMemo(
    () => (compareEnabled && previousCampaigns ? groupByType(previousCampaigns) : {}),
    [compareEnabled, previousCampaigns, getCampaignType]
  );

  /**
   * Les types de campagne, du plus coûteux au moins coûteux.
   *
   * Ils sortaient dans l'ordre où l'objet les avait reçus — c'est-à-dire dans celui où la régie
   * a rendu les campagnes, qui ne veut rien dire. Ce tableau est le seul du dashboard à n'avoir
   * jamais porté de tri : ses en-têtes annoncent pourtant `sortable`, sans écouteur derrière.
   *
   * À défaut de rendre les colonnes cliquables — ce qui est un autre chantier — il se range au
   * moins comme ses voisins : par coût décroissant, l'ordre dans lequel on lit un dashboard
   * publicitaire.
   */
  const rows = Object.values(typeGroups).sort((a, b) => (b.cost || 0) - (a.cost || 0));

  const sumTotals = (groups) => {
    const totals = Object.values(groups).reduce((acc, type) => ({
      impressions: acc.impressions + (type.impressions || 0),
      clicks: acc.clicks + (type.clicks || 0),
      cost: acc.cost + (type.cost || 0),
      conversions: acc.conversions + (type.conversions || 0),
      conversionsValue: acc.conversionsValue + (type.conversionsValue || 0),
      campaignCount: acc.campaignCount + (type.campaignCount || 0),
      ...cumulerVideo(acc, type)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, campaignCount: 0, ...TOTAUX_VIDEO_INITIAUX });
    // Ratios de sommes, jamais de moyennes de ratios.
    totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
    totals.cpc = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
    totals.conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
    totals.cpa = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
    totals.cos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;
    return totals;
  };

  const totals = sumTotals(typeGroups);
  const hasPrevious = compareEnabled && Object.keys(previousTypeGroups).length > 0;
  const previousTotals = hasPrevious ? sumTotals(previousTypeGroups) : null;

  if (rows.length === 0 && !loading) return null;
  if (userRole === 'CLIENT' && !isClientModuleVisible(visibilityModuleId)) return null;

  const visibleMetrics = (metrics?.order || []).filter(
    (key) => metrics?.metrics?.includes(key) && metricConfig[key]
  );
  const labelFor = (key) => getMetricLabel(key, moduleId, accountId) || metricConfig[key].defaultLabel;

  return (
    <div className="campaigns-table-section campaign-types-container">
      <div className="section-header">
        <h4><Target size={16} />&nbsp;{componentTitle}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          <ModuleVisibilityToggle
            isVisible={isClientModuleVisible(visibilityModuleId)}
            onToggle={toggleClientModuleVisibility}
            userRole={userRole}
            moduleId={visibilityModuleId}
          />
        </div>
      </div>

      {(userRole !== 'CLIENT' || isClientModuleVisible(visibilityModuleId)) && loadingComparison && (
        <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
          Chargement des données de comparaison...
        </div>
      )}

      {(userRole !== 'CLIENT' || isClientModuleVisible(visibilityModuleId)) && !loadingComparison && (
        <>
          {showCharts && (
            <CampaignTypesCharts
              campaignsData={campaigns}
              // CampaignTypesCharts transmet la valeur brute du type, pas la campagne :
              // on la ré-emballe pour réutiliser la même fonction que le tableau.
              getChannelTypeName={(rawType) =>
                getCampaignType({ advertisingChannelType: rawType, type: rawType })}
              COLORS={CHART_COLORS}
            />
          )}

          <div className="campaign-types-table-wrapper">
            <table className="campaign-types-table" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th className="sortable">Type</th>
                  <th className="sortable numeric">Campagnes</th>
                  {visibleMetrics.map((key) => (
                    <th key={key} className="sortable numeric">
                      {labelFor(key)}
                      {/* Le repère de tri, sur la seule colonne qui ordonne le tableau. Sans lui,
                          rien ne dirait pourquoi les lignes sont dans cet ordre. */}
                      {metricConfig[key]?.sortKey === 'cost' && ' ▼'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const previous = hasPrevious ? previousTypeGroups[row.type] : null;
                  return (
                    <tr
                      key={row.type || index}
                      onClick={onTypeClick ? () => onTypeClick(row.type) : undefined}
                      style={onTypeClick ? { cursor: 'pointer' } : undefined}
                      title={onTypeClick ? `Cliquer pour filtrer par type « ${row.type} »` : undefined}
                      onMouseOver={onTypeClick ? (e) => { e.currentTarget.style.backgroundColor = '#f0f9ff'; } : undefined}
                      onMouseOut={onTypeClick ? (e) => { e.currentTarget.style.backgroundColor = 'transparent'; } : undefined}
                    >
                      <td className="campaign-name">
                        {/* Pastille assortie à la part correspondante du camembert */}
                        <span
                          className="legend-dot"
                          style={{ backgroundColor: getCampaignTypeColor(row.type, CHART_COLORS) }}
                        />
                        {row.type}
                      </td>
                      <td className="numeric">{formatNumber(row.campaignCount || 0)}</td>
                      {visibleMetrics.map((key) => (
                        <React.Fragment key={key}>
                          {metricConfig[key].render(row, previous)}
                        </React.Fragment>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td className="campaign-name"><strong>TOTAL</strong></td>
                  <td className="numeric"><strong>{formatNumber(totals.campaignCount)}</strong></td>
                  {visibleMetrics.map((key) => (
                    <React.Fragment key={key}>
                      {metricConfig[key].renderTotal(totals, previousTotals)}
                    </React.Fragment>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      <MetricsConfigModal
        isOpen={showConfig}
        onClose={() => setShowConfig(false)}
        componentId={moduleId}
        accountId={accountId}
        componentTitle={componentTitle}
      />
    </div>
  );
};

export default CampaignTypesSection;
